import { StarOutlineRounded, StarRounded } from "@mui/icons-material";
import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LazyLoadImage, LazyLoadImageProps } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useParams } from "react-router-dom";
import DeleteIcon from "../../../assets/images/icons/DeleteIcon.tsx";
import DownloadIcon from "../../../assets/images/icons/DownloadIcon.tsx";
import FullscreenIcon from "../../../assets/images/icons/Image/FullscreenIcon";
import ImageComparisonIcon from "../../../assets/images/icons/ImageComparisonIcon.tsx";
import { APPS } from "../../../constants/AppsConstants.ts";
import { HubgalleryImage } from "../../../constants/HubGalleryImagesConstants.ts";
import { PollingFileStatus, ZERO_BYTE_IMAGE_ERROR_MESSAGE } from "../../../constants/ImageToImageConstants.ts";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { ImageEditingViewMode, PlaygroundImage } from "../../../models/image-to-image.ts";
import { TextToImageConfigType } from "../../../models/text-to-image.ts";
import iframeStore from "../../../pages/IframeNew/iframe-store.tsx";
import { generateStructuredPromptJsonFile } from "../../../pages/NewTG/utils/tg-utils.ts";
import { isAutomotiveApp, isAutomotiveUploadedPage, isFiboApp } from "../../../utils/index.ts";
import useImageUtils, { AsyncProps } from "../../../utils/useImageUtils";
import ApiReferenceButton from "../ApiReferenceButton/ApiReferenceButton.tsx";
import BriaButton from "../BriaButton/BriaButton.tsx";
import BriaTooltip from "../BriaTooltip/BriaTooltip";
import C2PAButton from "../C2PAButton/C2PAButton.tsx";
import { AspectRatio } from "../DropDowns/StaticDropdown";
import FullScreenViewer from "../FullScreenViewer/FullScreenViewer";
import { FullScreenImageType } from "../FullScreenViewer/Image/FullScreenImage";
import ImageError from "../ImageError/ImageError";
import LoadingAnimation from "../LoadingAnimation/LoadingAnimation.tsx";
import "../Selectable/Selected.scss";
import { SkeletonImage } from "../Skeleton/SkeletonGallery";
import styles from "./BriaImage.module.scss";

export type ImageErrorType = "offensive" | "unavailable" | "contentModeration" | "contentModerationAutomotive";

export type BriaImageProps = {
	image: (BriaImageProps & PlaygroundImage) | FullScreenImageType | HubgalleryImage;
	url: string;
	handleClick?: (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
	handleFullScreen?: () => void;
	handleCompare?: () => void;
	onZoom?: (zoomed: boolean) => void;
	hideFullScreenButton?: boolean;
	hideDownloadIcon?: boolean;
	aspectRatio?: AspectRatio;
	ImageOverlay?: ReactNode;
	displayOverlay?: "on" | "off" | "hover" | "customHover" | "fullScreenHover";
	loading?: boolean;
	errorType?: ImageErrorType;
	selectable?: boolean;
	selected?: boolean;
	className?: string;
	wrapperClassName?: string;
	skeletonClassName?: string;
	htmlJsonData?: any;
	async?: boolean;
	zoomable?: boolean;
	onSuccessPulling?: (result?: any) => void;
	onErrorPulling?: (errorType: ImageErrorType) => void;
	lazyImageProps?: LazyLoadImageProps;
	fullScreenProps?: Partial<FullScreenImageType & { fullScreenOnClick?: boolean }>;
	asyncProps?: AsyncProps;
	zoomProps?: ZoomProps;
	onDownload?: () => void;
	imageErrorCallBack?: (visual_hash?: string) => void;
	visualHash?: string;
	downloadProps?: { instantDownload?: boolean; fileName?: string };
	isProductPlacement?: boolean;
	disabled?: boolean;
	actionsClassName?: string;
	hideDeleteIcon?: boolean;
	hideFavoriteIcon?: boolean;
	hideApiReferenceButton?: boolean;
	is_favorite?: boolean;
	hidec2paIcon?: boolean;
	showTransparentGridBackground?: boolean;
	gridView?: boolean;
	downloadIcon?: React.ComponentType<any>;
	fullScreenIcon?: React.ComponentType<any>;
	showCompareButton?: boolean;
	showConstantOverlay?: boolean;
};

const BriaImage = ({
	image,
	url,
	onDownload,
	handleClick,
	handleFullScreen,
	handleCompare,
	hideFullScreenButton = false,
	hideDownloadIcon = true,
	hideApiReferenceButton = true,
	aspectRatio = "auto" as AspectRatio,
	ImageOverlay,
	displayOverlay = "customHover",
	loading,
	errorType,
	selectable = false,
	selected,
	className,
	wrapperClassName,
	skeletonClassName,
	htmlJsonData,
	async,
	zoomable,
	onZoom,
	onSuccessPulling,
	onErrorPulling,
	lazyImageProps,
	fullScreenProps,
	asyncProps,
	zoomProps = { scale: 6 },
	downloadProps,
	imageErrorCallBack,
	visualHash,
	isProductPlacement = false,
	disabled = false,
	actionsClassName,
	hideDeleteIcon,
	hideFavoriteIcon,
	hidec2paIcon,
	showTransparentGridBackground = false,
	gridView = false,
	downloadIcon: DownloadIconComponent,
	fullScreenIcon: FullScreenIconComponent,
	showCompareButton = false,
	showConstantOverlay = false,
}: BriaImageProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "briaImage" });
	const { isBlob, pullUntilAvailable, pullUntilAvailableV2, checkIfImageZeroByte, exportImage } = useImageUtils();
	const { playgroundStore, uiStore, textToImageStore } = useAppStore();
	const { projectsStore, sessionsStore } = playgroundStore;
	const { project_id, session_id } = useParams();
	const isSingleMode = playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE;
	const [imageUrl, setImageUrl] = useState(url);
	const [error, setError] = useState(errorType);
	const [localLoading, setLocalLoading] = useState(loading);
	const [isLazyLoaded, setIsLazyLoaded] = useState(false);
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	const [openCompareMode, setOpenCompareMode] = useState(false);
	const [zoomed, setZoomed] = useState(false);
	const [offsetX, setOffsetX] = useState(0);
	const [offsetY, setOffsetY] = useState(0);
	const imageRef = useRef<HTMLImageElement>(null);
	const isHoverDisplayOverlay = displayOverlay === "customHover" && !error;
	const [isImageAvailable, setIsImageAvailable] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const showApiReferenceButton =
		!hideApiReferenceButton &&
		(image as PlaygroundImage).type !== APPS.CAMPAIGN &&
		(image as PlaygroundImage).type !== "upload";

	useEffect(() => {
		const fetchImage = async () => {
			try {
				if (async && ((url && url.length > 0) || asyncProps?.isV2) && !isBlob(url)) {
					if (isImageAvailable) return;
					setLocalLoading(true);
					if (asyncProps?.isV2 && asyncProps?.statusUrl) {
						const result = await pullUntilAvailableV2(url, asyncProps);
						setLocalLoading(false);
						// Extract image_url from result object
						const finalUrl = result?.image_url || url;
						setImageUrl(finalUrl);
						// Pass the complete result to onSuccessPulling so we can extract structured_prompt
						onSuccessPulling?.(result);
					} else if (url && url.length > 0) {
						// V1 polling (original behavior)
						await pullUntilAvailable(url, asyncProps);
						setImageUrl(url);
						onSuccessPulling?.();
					}
					setIsImageAvailable(true);
				} else if (url && url.length > 0) {
					if (
						(url && url.length > 0 && (await checkIfImageZeroByte(url))) ||
						(image as any)?.errorType === PollingFileStatus.CONTENT_MODERATION
					)
						throw new Error(ZERO_BYTE_IMAGE_ERROR_MESSAGE);
					setImageUrl(url);
					setIsImageAvailable(true);
				}
			} catch (e: any) {
				// Handle abort errors silently (don't show error UI)
				if (e.message === "Aborted") {
					setLocalLoading(false);
					setIsImageAvailable(false);
					return;
				}

				// Check for content moderation error from V2 polling
				if (e.message === "CONTENT_MODERATION_ERROR" || (e as any).isContentModeration) {
					const errorType = isAutomotiveApp()
						? PollingFileStatus.CONTENT_MODERATION_AUTOMOTIVE
						: PollingFileStatus.CONTENT_MODERATION;
					setError(errorType);
					onErrorPulling?.(errorType);
				} else if (e.message && e.message === ZERO_BYTE_IMAGE_ERROR_MESSAGE) {
					const errorType = isAutomotiveApp()
						? PollingFileStatus.CONTENT_MODERATION_AUTOMOTIVE
						: PollingFileStatus.CONTENT_MODERATION;
					setError(errorType);
					onErrorPulling?.(errorType);
				} else {
					setError(PollingFileStatus.UNAVAILABLE);
					onErrorPulling?.(PollingFileStatus.UNAVAILABLE);
					setIsImageAvailable(false);
				}
			} finally {
				if (async && url && url.length > 0 && !isBlob(url)) {
					setLocalLoading(false);
				}
			}
		};

		fetchImage();
	}, [url, errorType, asyncProps?.statusUrl, asyncProps?.apiToken, asyncProps?.isV2, textToImageStore]);
	useEffect(() => {
		if (errorType) {
			setError(errorType);
			setLocalLoading(false);
			return;
		}
	}, [errorType]);

	// Sync localLoading with the loading prop when it changes
	useEffect(() => {
		if (loading !== undefined) {
			setLocalLoading(loading);
		}
	}, [loading]);

	const onFullScreen = (e: React.MouseEvent<SVGSVGElement>) => {
		e.stopPropagation();
		handleFullScreen ? handleFullScreen() : setIsViewerOpen(true);
	};

	const handleImageLoad = (event?: React.SyntheticEvent<HTMLImageElement, Event>) => {
		setIsLazyLoaded(true);
		if (lazyImageProps?.onLoad && event) {
			lazyImageProps.onLoad(event);
		}

		// Check This image inside the PlaygroundImages
		const isPlaygroundImage = (image as PlaygroundImage)?.id !== undefined;
		const isInPlaygroundImages = isPlaygroundImage
			? playgroundStore.playgroundResults.some((result) =>
					result.images?.some((img) => img.id === (image as PlaygroundImage).id),
				)
			: false;

		// Triggers react-joyride to recalculate the position of the tooltip
		isInPlaygroundImages && window.dispatchEvent(new Event("resize"));
	};

	const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
		event.preventDefault();
		event.stopPropagation();
		if (error || disabled || !isImageAvailable) return;
		if (zoomable) {
			if (zoomed) {
				onZoom && onZoom(false);
				setZoomed(false);
				setOffsetX(0);
				setOffsetY(0);
			} else {
				onZoom && onZoom(true);
				const rect = imageRef.current?.getBoundingClientRect();
				const x = event.clientX - (rect?.left || 0);
				const y = event.clientY - (rect?.top || 0);
				setOffsetX(x);
				setOffsetY(y);
				setZoomed(true);
			}
		}

		if (fullScreenProps?.fullScreenOnClick) {
			handleFullScreen?.();
		}

		handleClick?.();
	};
	const handleZoomedScroll = (event: React.WheelEvent<HTMLImageElement>) => {
		if (zoomed) {
			const { width = 0, height = 0 } = imageRef.current?.getBoundingClientRect() || {};
			setOffsetX((prevOffsetX) => Math.max(0, Math.min(prevOffsetX + event.deltaX, width)));
			setOffsetY((prevOffsetY) => Math.max(0, Math.min(prevOffsetY + event.deltaY, height)));
		}
	};

	const onImageError = (visual_hash?: string) => {
		if (imageErrorCallBack) {
			imageErrorCallBack(visual_hash);
		}
	};

	const handleDownload = async () => {
		const playgroundImage = image as PlaygroundImage;
		const imageConfig = playgroundImage?.config as TextToImageConfigType | undefined;

		if (imageConfig) {
			const baseFileName = downloadProps?.fileName || playgroundStore.getFileName(playgroundImage);
			const structured_prompt = playgroundImage.structured_prompt ?? imageConfig.structured_prompt;
			uiStore.showExportImagePopup({
				images: [playgroundImage],
				downloadProps: { ...downloadProps, fileName: baseFileName },
				onDownload,
				additionalFiles:
					structured_prompt != null && structured_prompt !== ""
						? [generateStructuredPromptJsonFile(structured_prompt, "structured_prompt.json")]
						: undefined,
			});
		} else if (downloadProps?.instantDownload) {
			await exportImage(
				url,
				downloadProps.fileName || playgroundStore.getFileName(playgroundImage),
				1,
				"download",
			);
			onDownload?.();
		} else {
			uiStore.showExportImagePopup({
				images: [playgroundImage],
				downloadProps,
				onDownload,
			});
		}
	};

	const handleAddToFavorite = async () => {
		if (isAutomotiveApp()) {
			await projectsStore.handleAddImageToFavorite(
				{ ...image, playground_result_id: (image as PlaygroundImage)?.playground_result_id } as PlaygroundImage,
				!image?.is_favorite,
			);
			session_id ? updateSessionImages(session_id) : updateProjectImages();
		}
	};

	const handleDelete = async () => {
		if (isAutomotiveApp()) {
			await projectsStore.handleDeleteImage((image as PlaygroundImage).id);
			session_id ? updateSessionImages(session_id) : updateProjectImages();
		}
	};

	const updateSessionImages = (session_id: string) => {
		sessionsStore.getSession(session_id);
	};

	const updateProjectImages = () => {
		isAutomotiveUploadedPage()
			? project_id && projectsStore.getUploadedImages(project_id, 1)
			: project_id && projectsStore.getFavoriteImages(project_id, 1);
	};

	const getUpdatedAspectRatio = () => {
		return aspectRatio === ("auto" as AspectRatio) ? "auto" : aspectRatio.replace(":", "/");
	};

	const handleCompareButton = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (handleCompare) {
			handleCompare();
		} else {
			setOpenCompareMode(true);
			setIsViewerOpen(true);
		}
	};

	return (
		<>
			{localLoading || loading ? (
				isSingleMode ? (
					<LoadingAnimation
						loading={localLoading || loading || false}
						singleModeLoader
						aspectRatio={
							isFiboApp() ? (image as BriaImageProps & PlaygroundImage)?.aspect_ratio : undefined
						}
					/>
				) : (
					<SkeletonImage aspectRatio={aspectRatio} className={skeletonClassName} />
				)
			) : (
				<>
					<Box
						ref={imageRef}
						className={clsx(
							wrapperClassName,
							styles.imageWrapper,
							(imageUrl || (image && (image as any)?.errorType)) && selectable && "selectableImage",
							selectable && selected && "selected",
							{
								[styles.zoomable]: zoomable,
								[styles.zoomed]: zoomed,
								[styles.isProductPlacement]: isProductPlacement,
								[styles.disabled]: disabled,
								[styles.singleMode]: isSingleMode,
								[styles.errorBorder]: error && isSingleMode,
								[styles.transparentGridBackground]: showTransparentGridBackground,
								[styles.gridView]: gridView,
								[styles.unavailable]: errorType === PollingFileStatus.UNAVAILABLE,
							},
						)}
						onClick={handleImageClick}
						onWheel={handleZoomedScroll}
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
						component="span"
						sx={{
							aspectRatio: getUpdatedAspectRatio(),
							opacity: 1,
						}}
						aria-valuetext={JSON.stringify(htmlJsonData)}
					>
						<Box
							className={clsx(styles.overLayer, {
								[styles.visibleOverlay]: showConstantOverlay,
								[styles.displayHover]: isHoverDisplayOverlay,
								[styles.errorOverlay]: error,
							})}
						>
							<>
								{!error ? (
									<>
										<Box
											className={clsx(styles.iconContainer, {
												[styles.singleMode]:
													playgroundStore.imageEditingViewMode ===
													ImageEditingViewMode.SINGLE,
											})}
										>
											<Box className={clsx(actionsClassName, styles.iconsWrapper)}>
												{!iframeStore.isIframe() && !hidec2paIcon && isImageAvailable && (
													<C2PAButton
														imageUrl={imageUrl}
														isHovered={isHovered}
														btnClassName={styles.c2paBtn}
													/>
												)}
												{showApiReferenceButton && (
													<ApiReferenceButton
														image={image as PlaygroundImage}
														tooltip="View API Reference"
													/>
												)}

												{isAutomotiveApp() && !hideFavoriteIcon && (
													<BriaTooltip title={t("fullScreenView.overlay.favorite")}>
														<BriaButton
															buttonType="tertiaryMedium"
															className={clsx(
																styles.playgroundActionButton,
																styles.favIcon,
																"iconHover",
															)}
															onClick={handleAddToFavorite}
														>
															{image?.is_favorite ? (
																<StarRounded />
															) : (
																<StarOutlineRounded />
															)}
														</BriaButton>
													</BriaTooltip>
												)}
											</Box>
											<Box className={styles.iconsWrapper}>
												{!hideDownloadIcon &&
													(!iframeStore.isIframe() ||
														iframeStore.iframe.config.general_config?.enable_download ||
														iframeStore.iframe.config.general_config?.enable_save) && (
														<BriaTooltip title={t("fullScreenView.overlay.download")}>
															<BriaButton
																buttonType="tertiaryMedium"
																className={clsx(
																	styles.playgroundActionButton,
																	"iconHover",
																)}
																onClick={(e) => {
																	e.stopPropagation();
																	handleDownload();
																}}
															>
																{DownloadIconComponent ? (
																	<DownloadIconComponent />
																) : (
																	<DownloadIcon />
																)}
															</BriaButton>
														</BriaTooltip>
													)}

												{isAutomotiveApp() && !hideDeleteIcon && (
													<BriaTooltip title={t("fullScreenView.overlay.delete")}>
														<BriaButton
															buttonType="tertiaryMedium"
															className={clsx(styles.playgroundActionButton, "iconHover")}
															onClick={(e) => {
																e.stopPropagation();
																handleDelete();
															}}
														>
															<DeleteIcon />
														</BriaButton>
													</BriaTooltip>
												)}
												{!hideFullScreenButton && (
													<BriaTooltip title={t("fullScreenView.tooltip")}>
														{FullScreenIconComponent ? (
															<BriaButton
																buttonType="tertiaryMedium"
																className={clsx(styles.playgroundActionButton, {
																	["iconHover"]:
																		isHoverDisplayOverlay ||
																		displayOverlay === "fullScreenHover",
																})}
																onClick={(e: any) => {
																	onFullScreen(e);
																}}
															>
																<FullScreenIconComponent />
															</BriaButton>
														) : (
															<BriaButton
																buttonType="tertiaryMedium"
																className={clsx(styles.playgroundActionButton, {
																	["iconHover"]:
																		isHoverDisplayOverlay ||
																		displayOverlay === "fullScreenHover",
																})}
																onClick={(e: any) => {
																	onFullScreen(e);
																}}
															>
																<FullscreenIcon />
															</BriaButton>
														)}
													</BriaTooltip>
												)}
												{showCompareButton && (
													<BriaTooltip title={t("compareButtonTooltip")}>
														<BriaButton
															buttonType="tertiaryMedium"
															className={clsx(styles.playgroundActionButton, "iconHover")}
															onClick={handleCompareButton}
														>
															<ImageComparisonIcon />
														</BriaButton>
													</BriaTooltip>
												)}
											</Box>
										</Box>
										{displayOverlay !== "off" &&
											displayOverlay !== "fullScreenHover" &&
											ImageOverlay}
									</>
								) : (
									<ImageError
										imageWidth={isSingleMode ? `500px` : "100%"}
										imageHeight={`100%`}
										titleText={t(`errors.${error}.title`)}
										massageText={t(`errors.${error}.message`)}
										titleFontSize="14px"
										massageTextSize="14px"
										largeSvg
										warningSvg={
											error === PollingFileStatus.CONTENT_MODERATION ||
											error === PollingFileStatus.CONTENT_MODERATION_AUTOMOTIVE
										}
										aspectRatio={(image as BriaImageProps & PlaygroundImage)?.aspect_ratio}
									/>
								)}
							</>
						</Box>
						{!(error && isSingleMode) && (
							<LazyLoadImage
								className={`${className} ${styles.image} ${
									isLazyLoaded ? styles.loaded : styles.loading
								} ${error && styles.error}`}
								key={imageUrl}
								alt={imageUrl}
								placeholderSrc={t("lazyLoadPlaceholder")}
								effect="blur"
								src={imageUrl}
								threshold={100}
								width="100%"
								onLoad={handleImageLoad}
								style={{
									aspectRatio: getUpdatedAspectRatio(),
									opacity: 1,
									transformOrigin: `${offsetX}px ${offsetY}px`,
									transform: zoomed ? `scale(${zoomProps.scale})` : "",
								}}
								wrapperProps={{
									style: {
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										position: "relative", // Required to scope stacking
										zIndex: 1,
										aspectRatio: error ? getUpdatedAspectRatio() : undefined,
										overflow: error ? "hidden" : undefined,
									},
								}}
								wrapperClassName={className}
								loading="lazy"
								{...lazyImageProps}
								onError={() => onImageError(visualHash)}
							/>
						)}
					</Box>
					{isViewerOpen && (
						<FullScreenViewer
							open={isViewerOpen}
							images={[{ src: imageUrl, ...fullScreenProps, openCompare: openCompareMode }]}
							onClose={() => {
								setIsViewerOpen(false);
								setOpenCompareMode(false);
							}}
							onDownload={handleDownload}
						/>
					)}
				</>
			)}
		</>
	);
};

const ObservedComponent = observer(BriaImage);
export default ObservedComponent;

export type ZoomProps = {
	scale: number;
};
